import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { userManagementApi } from '../shared/api/userApi';

const businessTypes = [
  'Retail Store',
  'Wholesale Business',
  'Manufacturing',
  'Service Provider',
  'Restaurant/Food Service',
  'Healthcare',
  'Education',
  'Technology/IT',
  'Construction',
  'Transportation',
  'Real Estate',
  'Other'
];

const steps = ['Welcome', 'Business Details', 'Contact Information', 'Complete Setup'];

function ProfileSetup({ user, onComplete, onSkip }) {
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    businessName: '',
    businessType: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    phone: '',
    website: '',
    description: '',
    employeeCount: '',
    foundedYear: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Load existing business data on component mount
  React.useEffect(() => {
    const loadExistingBusinessData = async () => {
      try {
        setLoading(true);
        const businessResponse = await userManagementApi.getBusinessProfile(user.id);

        if (businessResponse.success && businessResponse.business) {
          const business = businessResponse.business;
          console.log('✅ Loaded existing business data:', business);

          // Populate form with existing data
          setProfileData({
            businessName: business.name || '',
            businessType: business.type || business.business_type || '',
            industry: business.industry || '',
            address: business.address || '',
            city: business.city || '',
            state: business.state || '',
            zipCode: business.zipCode || business.zip_code || '',
            country: business.country || 'India',
            phone: business.phone || '',
            website: business.website || '',
            description: business.description || '',
            employeeCount: business.employeeCount || business.employee_count || '',
            foundedYear: business.foundedYear || business.founded_year || '',
          });
        } else {
          console.log('ℹ️ No existing business data found, starting fresh');
        }
      } catch (error) {
        console.log('⚠️ Error loading existing business data:', error.message);
        // Continue with empty form if loading fails
      } finally {
        setLoading(false);
      }
    };

    loadExistingBusinessData();
  }, [user.id]);

  const handleNext = () => {
    if (activeStep === 1) {
      if (!profileData.businessName || fieldErrors.businessName) {
        setError(fieldErrors.businessName || 'Business name is required');
        return;
      }
      if (!profileData.businessType || fieldErrors.businessType) {
        setError(fieldErrors.businessType || 'Business type is required');
        return;
      }
    }
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateField = (field, value) => {
    const errors = { ...fieldErrors };

    switch (field) {
      case 'businessName':
        if (!value.trim()) {
          errors.businessName = 'Business name is required';
        } else if (value.trim().length < 2) {
          errors.businessName = 'Business name must be at least 2 characters';
        } else {
          delete errors.businessName;
        }
        break;
      case 'businessType':
        if (!value) {
          errors.businessType = 'Business type is required';
        } else {
          delete errors.businessType;
        }
        break;
      case 'phone':
        if (value && !/^\+?[\d\s\-\(\)]+$/.test(value)) {
          errors.phone = 'Please enter a valid phone number';
        } else {
          delete errors.phone;
        }
        break;
      case 'website':
        if (value && !/^https?:\/\/.+/.test(value)) {
          errors.website = 'Please enter a valid URL starting with http:// or https://';
        } else {
          delete errors.website;
        }
        break;
      case 'employeeCount':
        if (value && (isNaN(value) || parseInt(value) < 0)) {
          errors.employeeCount = 'Please enter a valid number';
        } else {
          delete errors.employeeCount;
        }
        break;
      case 'foundedYear':
        if (value && (isNaN(value) || parseInt(value) < 1800 || parseInt(value) > new Date().getFullYear())) {
          errors.foundedYear = 'Please enter a valid year';
        } else {
          delete errors.foundedYear;
        }
        break;
      default:
        break;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Validate the field immediately
    validateField(field, value);

    if (error) setError(null);
  };

  const handleComplete = async () => {
    if (!profileData.businessName || fieldErrors.businessName) {
      setError(fieldErrors.businessName || 'Business name is required');
      return;
    }
    if (!profileData.businessType || fieldErrors.businessType) {
      setError(fieldErrors.businessType || 'Business type is required');
      return;
    }

    // Check for any other field errors
    const hasErrors = Object.keys(fieldErrors).length > 0;
    if (hasErrors) {
      setError('Please fix the validation errors before continuing');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      let companyId = null;

      // Prepare business data for saving
      const businessData = {
        name: profileData.businessName,
        type: profileData.businessType,
        industry: profileData.industry,
        address: profileData.address,
        city: profileData.city,
        state: profileData.state,
        zipCode: profileData.zipCode,
        country: profileData.country,
        phone: profileData.phone,
        website: profileData.website,
        description: profileData.description,
        employeeCount: profileData.employeeCount,
        foundedYear: profileData.foundedYear,
      };

      // Save to backend - use PUT for updates, POST for new businesses
      try {
        let existingBusiness = null;

        // Check if business already exists by trying to get it
        try {
          existingBusiness = await userManagementApi.getBusinessProfile(user.id);
        } catch (getError) {
          // Expected for new users - no existing business
          console.log('ℹ️ No existing business found, will create new one');
        }

        let businessResponse;
        if (existingBusiness && existingBusiness.success && existingBusiness.business) {
          // Update existing business
          console.log('🔄 Updating existing business profile...');
          businessResponse = await userManagementApi.updateBusinessProfile(user.id, businessData);
        } else {
          // Create new business
          console.log('🆕 Creating new business profile...');
          businessResponse = await userManagementApi.saveBusinessProfile(user.id, businessData);
        }

        // Extract company/business ID from response if available
        companyId = businessResponse?.company?.id || businessResponse?.business?.id || businessResponse?.id;

        console.log('✅ Business profile saved successfully');
      } catch (apiError) {
        console.error('❌ Error saving business profile:', apiError);
        setError('Failed to save business profile. Please try again.');
        return;
      }

      // Pass the data to the parent component, including company ID if available
      const completeBusinessData = {
        ...businessData,
        ...(companyId && { id: companyId }),
      };
      await onComplete(completeBusinessData);
    } catch (err) {
      console.error('❌ Error completing setup:', err);
      setError('Failed to complete setup. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        const hasExistingBusiness = profileData.businessName && profileData.businessType;
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar
              src={user.imageUrl}
              alt={user.name}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 3,
                border: '4px solid white',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Welcome back, {user.name.split(' ')[0]}! 👋
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
              {hasExistingBusiness ? 'Update your business profile' : 'Let\'s set up your business profile'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto', lineHeight: 1.6 }}>
              {hasExistingBusiness
                ? 'We found your existing business profile. You can update any information or keep it as is.'
                : 'We\'ll help you create a professional business profile that will be used throughout the system. This information helps us provide personalized insights and better manage your business operations.'
              }
            </Typography>

            {hasExistingBusiness && (
              <Box sx={{ mt: 3, p: 2, background: 'rgba(16, 185, 129, 0.1)', borderRadius: 2, border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: '#10b981', fontWeight: 600, mb: 1 }}>
                  Existing Business Found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {profileData.businessName} - {profileData.businessType}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label="Secure & Private"
                sx={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}
              />
              <Chip
                icon={<CheckCircleIcon />}
                label="Easy to Update"
                sx={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}
              />
              <Chip
                icon={<CheckCircleIcon />}
                label="Professional Branding"
                sx={{ background: 'rgba(147, 51, 234, 0.1)', color: '#9333ea' }}
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Business Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Business Name"
                value={profileData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                required
                variant="outlined"
                error={!!fieldErrors.businessName}
                helperText={fieldErrors.businessName}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <FormControl fullWidth required error={!!fieldErrors.businessType}>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={profileData.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  label="Business Type"
                  sx={{ borderRadius: 2 }}
                >
                  {businessTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.businessType && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {fieldErrors.businessType}
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Industry/Sector (Optional)"
                value={profileData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                placeholder="e.g., Electronics, Fashion, Food & Beverage"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label="Business Description (Optional)"
                value={profileData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                placeholder="Tell us about your business..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Employee Count (Optional)"
                  value={profileData.employeeCount}
                  onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                  placeholder="e.g., 1-10, 11-50, 51-200"
                  variant="outlined"
                  error={!!fieldErrors.employeeCount}
                  helperText={fieldErrors.employeeCount}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Founded Year (Optional)"
                  value={profileData.foundedYear}
                  onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                  placeholder="e.g., 2020"
                  variant="outlined"
                  error={!!fieldErrors.foundedYear}
                  helperText={fieldErrors.foundedYear}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Contact Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={profileData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+91 9876543210"
                variant="outlined"
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label="Website (Optional)"
                value={profileData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://www.yourbusiness.com"
                variant="outlined"
                error={!!fieldErrors.website}
                helperText={fieldErrors.website}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label="Street Address"
                value={profileData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                multiline
                rows={2}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={profileData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="State/Province"
                  value={profileData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={profileData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Country"
                  value={profileData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        );

      case 3:
        const isUpdate = profileData.businessName && profileData.businessType;
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 50, color: 'white' }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {isUpdate ? 'Profile Updated! 🎉' : 'All Set! 🎉'}
            </Typography>

            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
              Your business profile is {isUpdate ? 'updated' : 'ready'}
            </Typography>

            <Card sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {profileData.businessName || 'Your Business'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {profileData.businessType || 'Business Type'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {profileData.city ? `${profileData.city}, ${profileData.country}` : 'Location'}
              </Typography>
            </Card>

            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
              {isUpdate
                ? 'Your business profile has been successfully updated! You can always make further changes from the settings page.'
                : 'You\'re all set to start managing your business! You can always update your profile information from the settings page.'
              }
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  // Show loading spinner while fetching existing data
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            p: 4,
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Loading your business profile...
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            color: 'white',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
            Business Profile Setup
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, opacity: 0.9 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        {/* Stepper */}
        <Box sx={{ px: 3, py: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <CardContent sx={{ px: 4, py: 2, minHeight: 400 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}
        </CardContent>

        {/* Actions */}
        <Box sx={{ px: 4, py: 3, background: 'rgba(0, 0, 0, 0.02)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Skip Setup button removed - users must complete the full setup */}

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={saving}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    }
                  }}
                >
                  {saving ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Complete Setup'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    }
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export default ProfileSetup;
